/**
 * Control de Configuración — vista de cambios detectados que hay que revertir.
 *
 * A diferencia de "Auditoría de config" (centrada en perfiles), esta pantalla
 * pone el drift al frente: una fila = un CAMPO que cambió en un equipo, con
 * "actual vs esperado" lado a lado y acción de revertir (individual o masiva).
 *
 * La zona horaria es el caso modelo (su "esperado" viene del mapa canónico por
 * región), pero la vista sirve igual para voz/NTP/red/firmware/servicios.
 *
 * Scope inicial = Holanda. El diseño es genérico por sitio/tipo de equipo, así
 * que replicarlo a la red de tiLatina es la misma pantalla con otro scope.
 */
import {
  AlertTriangle,
  Clock,
  Cpu,
  Loader2,
  Lock,
  Network,
  RefreshCw,
  RotateCcw,
  Volume2,
  WifiOff,
  Wrench,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { ApiError } from "@/api/client";
import type {
  ConfigDriftRow,
  DriftSeverity,
  RecosechaEstado,
} from "@/api/types";
import { PlanchadoDialog } from "@/components/PlanchadoDialog";
import { RollbackDialog } from "@/components/RollbackDialog";
import {
  useBiometricos,
  useConfigDrift,
  usePlanchado,
  useRecosechaEstado,
  useRecosechar,
  useRevertDriftField,
  useTakeSnapshot,
} from "@/hooks/useConfig";
import { cn } from "@/lib/utils";
import { CamarasPanel } from "@/components/CamarasPanel";
import { PlancharFlota } from "@/components/PlancharFlota";
import { GrabadoresPanel } from "@/components/GrabadoresPanel";
import { PanelesPanel } from "@/components/PanelesPanel";
import { HikCentralPanel } from "@/components/HikCentralPanel";
import { FlotaRutPanel } from "@/components/FlotaRutPanel";
import { InventarioLanPanel } from "@/components/InventarioLanPanel";
import { ResumenCobertura } from "@/components/ConfigComunes";

// Las rutelas de tiLatina (hoy la puerta del piso 1) son el mismo tipo de
// equipo que las de Holanda pero de otro cliente: comparten motor y pestaña de
// codigo, y se separan por el scope (= cliente del sitio). Esta clave de
// pestaña NO es un device_type: se traduce abajo a `tipoApi` + `scope`.
const RUT_TILATINA = "rut_tilatina";

type ViewMode = "field" | "site";

// Objetivo de la reversión a confirmar en el modal (1 equipo o N por campo).
interface RevertTarget {
  field: string;
  fieldLabel: string;
  deviceIds: string[];
  label: string;
  helper: string;
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "nunca";
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "hace segundos";
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86_400) return `hace ${Math.floor(seconds / 3600)} h`;
  return `hace ${Math.floor(seconds / 86_400)} d`;
}

function FieldIcon({ field }: { field: string }) {
  const cls = "size-4 shrink-0 text-muted-foreground";
  if (field === "_sin_datos") return <WifiOff className={cls} />;
  if (field.startsWith("time.timezone")) return <Clock className={cls} />;
  if (field.startsWith("time")) return <Clock className={cls} />;
  if (field.startsWith("access") || field.includes("voice"))
    return <Volume2 className={cls} />;
  if (field.startsWith("network")) return <Network className={cls} />;
  if (field.startsWith("services")) return <Lock className={cls} />;
  if (field.startsWith("firmware")) return <Cpu className={cls} />;
  return <Wrench className={cls} />;
}

const SEVERITY_STYLE: Record<DriftSeverity, string> = {
  high: "bg-rose-500/15 text-rose-700 dark:text-rose-400",
  medium: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  low: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
};
const SEVERITY_LABEL: Record<DriftSeverity, string> = {
  high: "Crítico",
  medium: "Leve",
  low: "Menor",
};

function SeverityBadge({ value }: { value: DriftSeverity }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
        SEVERITY_STYLE[value],
      )}
    >
      ● {SEVERITY_LABEL[value]}
    </span>
  );
}

export function ControlConfiguracionPage() {
  const [viewMode, setViewMode] = useState<ViewMode>("field");
  const [fieldFilter, setFieldFilter] = useState<string>("");
  const [siteFilter, setSiteFilter] = useState<string>("");
  const [severityFilter, setSeverityFilter] = useState<string>("");
  const [target, setTarget] = useState<RevertTarget | null>(null);
  const [deviceType, setDeviceType] = useState<string>("hikvision_biometric");
  const [planchadoTarget, setPlanchadoTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const esRutTilatina = deviceType === RUT_TILATINA;
  const tipoApi = esRutTilatina ? "rut240_teltonika" : deviceType;
  const scope = esRutTilatina ? "tilatina" : "holanda";
  const cliente = esRutTilatina ? "tiLatina" : "Holanda";
  const esHikCentral = deviceType === "hikcentral_platform";
  const esCamaras = deviceType === "hikcentral_camaras";
  const esGrabadores = deviceType === "hikcentral_grabadores";
  const esPaneles = deviceType === "hikcentral_paneles";
  const esInventario = deviceType === "inventario_lan";
  // Ninguna de las dos vistas nuevas es deriva de configuracion: reemplazan la
  // pantalla en vez de filtrarla.
  const esVistaPropia = esHikCentral || esCamaras || esGrabadores || esPaneles || esInventario;
  const esBiometrico = deviceType === "hikvision_biometric";
  // La cabecera homologada de biométricos: sólo los totales; la deriva campo a
  // campo sigue siendo la tabla de abajo. Se pide sólo en esta pestaña.
  const biometricos = useBiometricos(esBiometrico);
  const esRouter = tipoApi === "rut240_teltonika";
  // Los dos tipos con golden y generador de comandos. El backend rechaza el
  // resto, asi que el boton no se ofrece donde no puede funcionar.
  const admitePlanchado = esRouter || esBiometrico;

  const drift = useConfigDrift({
    scope,
    device_type: tipoApi,
    field: fieldFilter || undefined,
    site: siteFilter || undefined,
    severity: (severityFilter as DriftSeverity) || undefined,
  }, !esVistaPropia);
  const revert = useRevertDriftField();
  const planchadoQuery = usePlanchado(planchadoTarget?.id ?? null);
  const recosechar = useRecosechar();
  const queryClient = useQueryClient();
  // Las vistas de camaras y grabadores se cosechan con el perfil de su equipo
  // (`hikvision_camera`, `hikvision_nvr`), no con su tipo de pestana: el avance
  // hay que pedirlo por ESE tipo, o el servidor contesta "no hay barrida" y la
  // pantalla se queda en "Cosechando…".
  const tipoCosecha = esCamaras ? "hikvision_camera" : esGrabadores ? "hikvision_nvr" : tipoApi;
  const conCosechaPropia = esCamaras || esGrabadores;

  // Hay una recosecha lanzada desde ESTA pantalla y aun no la damos por
  // terminada. Quien decide que termino es el servidor, no un reloj de aqui.
  const [cosechando, setCosechando] = useState(false);
  // Avance REAL del servidor. Antes esto lo gobernaba un temporizador ciego de
  // ~6 min en el cliente: decia "Cosecha terminada" mientras el servidor iba
  // por el equipo 30, y seguia diciendo "Cosechando..." minutos despues de
  // acabar. Ahora el servidor dice por donde va, que esta bajando y como
  // termino cada equipo. Ver GET /config/recosechar/estado.
  const avance = useRecosechaEstado(tipoCosecha, cosechando && (conCosechaPropia || !esVistaPropia));
  const est = avance.data;
  // El aviso se cierra a mano; una barrida nueva lo vuelve a abrir.
  const [avisoCerrado, setAvisoCerrado] = useState(false);
  useEffect(() => {
    if (!cosechando || !est?.terminado) return;
    setCosechando(false);
    if (esCamaras) void queryClient.invalidateQueries({ queryKey: ["config", "camaras"] });
    if (esGrabadores) void queryClient.invalidateQueries({ queryKey: ["config", "grabadores"] });
    // Un refetch final: el ultimo equipo de la barrida termino DESPUES del
    // refetch anterior y su fila todavia no esta en la tabla.
    void drift.refetch();
    // drift.refetch es estable (react-query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cosechando, est?.terminado]);
  // La tabla de deriva se recalcula en vivo desde el ultimo snapshot, asi que
  // hay que refrescarla mientras avanza. Se engancha al contador de equipos
  // terminados en vez de a un reloj propio: un refetch por equipo cosechado,
  // ni uno de mas.
  const hechos = est?.hechos ?? 0;
  useEffect(() => {
    if (!cosechando) return;
    // Camaras no tiene tabla de deriva: su vista se refresca UNA vez, al terminar.
    if (conCosechaPropia) return;
    void drift.refetch();
    // drift.refetch es estable (react-query).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hechos, cosechando]);

  const data = esVistaPropia ? undefined : drift.data;
  const rows = data?.rows ?? [];

  // Opciones de filtro derivadas del resultado.
  const sites = useMemo(
    () => Array.from(new Set(rows.map((r) => r.site))).sort(),
    [rows],
  );
  const fields = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) seen.set(r.field, r.field_label);
    return Array.from(seen.entries());
  }, [rows]);

  const handleConfirm = ({
    totpCode,
    notes,
  }: {
    totpCode: string;
    notes: string;
  }) => {
    if (!target) return;
    revert.mutate(
      {
        totp_code: totpCode,
        field: target.field,
        device_ids: target.deviceIds,
        notes: notes || null,
      },
      { onSuccess: () => setTarget(null) },
    );
  };

  const revertError =
    revert.error instanceof ApiError
      ? revert.error.message
      : revert.error
        ? "No se pudo encolar la reversión."
        : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex w-fit gap-1 rounded-md border bg-card p-1 text-sm">
          {[
            ["hikvision_biometric", "Biométricos"],
            ["rut240_teltonika", "RUT Holanda"],
            [RUT_TILATINA, "RUT tiLatina"],
            ["hikcentral_platform", "HikCentral"],
            ["hikcentral_camaras", "Cámaras"],
            ["hikcentral_grabadores", "Grabadores"],
            ["hikcentral_paneles", "Paneles"],
            ["inventario_lan", "Inventario LAN"],
          ].map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => setDeviceType(val)}
              className={cn(
                "rounded px-3 py-1",
                deviceType === val
                  ? "bg-muted font-medium"
                  : "text-muted-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* El boton vale para las dos vistas de camaras: son perfiles de
            cosecha como los demas y se disparan igual. Para HikCentral y el
            inventario de LAN sigue escondido — esos no tienen perfil propio
            que recosechar desde aqui. */}
        {esVistaPropia && !conCosechaPropia ? null : (
        <button
          type="button"
          title="Vuelve a cosechar todos los equipos ahora (en segundo plano) para ver el estado real"
          onClick={() =>
            recosechar.mutate(tipoCosecha, {
              onSuccess: () => {
                // Fuera el "terminado" de la barrida anterior: si se queda en
                // cache, el efecto de arriba apaga esta en el primer render.
                void queryClient.resetQueries({
                  queryKey: ["config", "recosecha-estado", tipoCosecha],
                });
                setAvisoCerrado(false);
                setCosechando(true);
              },
            })
          }
          disabled={recosechar.isPending || cosechando}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-60"
        >
          <RefreshCw
            className={cn(
              "size-3.5",
              (recosechar.isPending || cosechando) && "animate-spin",
            )}
          />
          {cosechando ? "Cosechando…" : "Cosechar ahora"}
        </button>
        )}
      </div>

      {/* HikCentral no es deriva de configuracion: es inventario + estado de
          toda la plataforma. Por eso reemplaza la vista en vez de filtrarla. */}
      {/* Qué hardware hay de verdad en la flota. Va en la vista de routers y
          NO reemplaza nada: es el agregado, la tabla de abajo sigue siendo el
          detalle. Con 0 deriva esa tabla queda vacía y esto sigue diciendo algo. */}
      {esRouter && <FlotaRutPanel cliente={cliente} />}

      {esHikCentral && <HikCentralPanel />}
      {esCamaras && <CamarasPanel />}
      {esGrabadores && <GrabadoresPanel />}
      {esPaneles && <PanelesPanel />}
      {esInventario && <InventarioLanPanel />}

      {!esVistaPropia && est?.hay_run && !avisoCerrado && (
        <AvanceCosecha est={est} onCerrar={() => setAvisoCerrado(true)} />
      )}
      {!esVistaPropia && recosechar.isError && !cosechando && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-400">
          {recosechar.error instanceof ApiError
            ? recosechar.error.message
            : "No se pudo iniciar la cosecha."}
        </div>
      )}
      {!esVistaPropia && (
      <Header
        scope={scope}
        devices={data?.summary.devices ?? 0}
        withDrift={data?.summary.with_drift ?? 0}
        timezoneCount={
          data?.summary.by_field.find((f) => f.field === "time.timezone")
            ?.count ?? 0
        }
        lastAuditAt={data?.last_audit_at ?? null}
        loading={drift.isFetching}
        mostrarKpis={!esBiometrico}
      />
      )}

      {/* Planchar toda la flota. Se abre EN SECO: ensena que cambiaria antes
          de tocar nada. "Revertir" de abajo solo registra la intencion; este
          si escribe en los equipos. */}
      <PlancharFlota />

      {/* Acciones masivas por campo (revertir todo X) */}
      {(data?.summary.by_field.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Revertir en masa:
          </span>
          {data!.summary.by_field.map((f) => {
            const ids = rows
              .filter((r) => r.field === f.field)
              .map((r) => r.device_id);
            return (
              <button
                key={f.field}
                type="button"
                disabled={ids.length === 0}
                onClick={() =>
                  setTarget({
                    field: f.field,
                    fieldLabel: f.field_label ?? f.field,
                    deviceIds: ids,
                    label: `${f.field_label ?? f.field} · ${ids.length} equipos (${cliente})`,
                    helper:
                      "Re-aplica el valor esperado en todos los equipos con este cambio.",
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-50"
              >
                <RotateCcw className="size-3.5" />
                {f.field_label ?? f.field}
                <span className="rounded bg-muted px-1.5 py-0.5 tabular-nums text-muted-foreground">
                  {f.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Cabecera homologada de biométricos, encima de su tabla de deriva. */}
      {esBiometrico &&
        biometricos.data?._status === "ok" &&
        biometricos.data.totales && (
          <ResumenCobertura
            totales={{
              total: biometricos.data.totales.total,
              leidos: biometricos.data.totales.leidos,
              cobertura_pct: biometricos.data.totales.cobertura_pct,
              cumplen: biometricos.data.totales.cumplen,
              revirtieron: biometricos.data.totales.revirtieron,
              fuera: biometricos.data.totales.fuera,
              criticos: biometricos.data.totales.criticos,
              sin_leer: biometricos.data.totales.sin_leer,
              no_responden: biometricos.data.totales.no_responden,
            }}
          />
        )}

      {/* Toggle de vista + filtros */}
      {!esVistaPropia && (
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border">
          <ToggleBtn
            active={viewMode === "field"}
            onClick={() => setViewMode("field")}
          >
            Por cambio
          </ToggleBtn>
          <ToggleBtn
            active={viewMode === "site"}
            onClick={() => setViewMode("site")}
          >
            Por agencia
          </ToggleBtn>
        </div>

        <select
          value={fieldFilter}
          onChange={(e) => setFieldFilter(e.target.value)}
          aria-label="Filtrar por campo"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Todos los campos</option>
          {fields.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          aria-label="Filtrar por agencia"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Todas las agencias</option>
          {sites.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
          aria-label="Filtrar por severidad"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Toda severidad</option>
          <option value="high">Crítico</option>
          <option value="medium">Leve</option>
          <option value="low">Menor</option>
        </select>
      </div>
      )}

      {esVistaPropia ? null : drift.isPending && !drift.data ? (
        <div className="flex items-center justify-center rounded-md border bg-card p-10">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : viewMode === "field" ? (
        <DriftTable
          rows={rows}
          onRevert={setTarget}
          onPlanchar={
          admitePlanchado
            ? (id, label) => setPlanchadoTarget({ id, label })
            : undefined
        }
        />
      ) : (
        <DriftBySite
          rows={rows}
          onRevert={setTarget}
          onPlanchar={
          admitePlanchado
            ? (id, label) => setPlanchadoTarget({ id, label })
            : undefined
        }
        />
      )}

      <RollbackDialog
        open={target !== null}
        onClose={() => {
          setTarget(null);
          revert.reset();
        }}
        onConfirm={handleConfirm}
        isSubmitting={revert.isPending}
        errorMessage={revertError}
        targetLabel={target?.label ?? ""}
        helperText={target?.helper}
      />

      <PlanchadoDialog
        open={planchadoTarget !== null}
        onClose={() => setPlanchadoTarget(null)}
        deviceId={planchadoTarget?.id ?? null}
        targetLabel={planchadoTarget?.label ?? ""}
        loading={planchadoQuery.isFetching}
        errorMessage={
          planchadoQuery.error instanceof ApiError
            ? planchadoQuery.error.message
            : planchadoQuery.error
              ? "No se pudieron generar los comandos."
              : null
        }
        enGolden={planchadoQuery.data?.en_golden ?? false}
        comandos={planchadoQuery.data?.comandos ?? ""}
        nombreEquipo={esBiometrico ? "biométrico" : "router"}
        dondeCorrerlos={
          esBiometrico
            ? "el servidor de AROC en la nube (ssh nube), que es el único con ruta a las LAN de agencia — exporta antes BIO_USER y BIO_PASS"
            : "la shell del RUT (SSH)"
        }
      />
    </div>
  );
}

function Header({
  scope,
  devices,
  withDrift,
  timezoneCount,
  lastAuditAt,
  loading,
  mostrarKpis = true,
}: {
  scope: string;
  devices: number;
  withDrift: number;
  timezoneCount: number;
  lastAuditAt: string | null;
  loading: boolean;
  // En biométricos los KPIs de aquí los reemplaza la barra ResumenCobertura,
  // que cuenta lo mismo mejor; mostrar las dos era ruido (lo pidió Edgar).
  mostrarKpis?: boolean;
}) {
  return (
    <header className="space-y-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Control de configuración
          <span className="ml-2 align-middle text-sm font-normal capitalize text-muted-foreground">
            · {scope}
          </span>
        </h1>
        <p className="text-sm text-muted-foreground">
          Cambios detectados en los equipos contra la configuración esperada.
          Revierte el valor correcto por equipo o en masa. La zona horaria es el
          caso recurrente.
        </p>
      </div>
      {mostrarKpis && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Kpi label="Equipos auditados" value={devices} />
          <Kpi label="Con cambios" value={withDrift} tone="amber" />
          <Kpi label="Zona horaria mal" value={timezoneCount} tone="rose" />
          <div className="flex flex-col justify-center rounded-md border bg-card px-4 py-3">
            <span className="text-xs text-muted-foreground">Última auditoría</span>
            <span className="mt-0.5 flex items-center gap-1.5 text-sm font-medium">
              {loading && (
                <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
              )}
              {formatRelative(lastAuditAt)}
            </span>
          </div>
        </div>
      )}
    </header>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "amber" | "rose";
}) {
  const valueCls =
    tone === "rose"
      ? "text-rose-600 dark:text-rose-400"
      : tone === "amber"
        ? "text-amber-600 dark:text-amber-400"
        : "text-foreground";
  return (
    <div className="rounded-md border bg-card px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("mt-0.5 text-2xl font-semibold tabular-nums", valueCls)}>
        {value}
      </div>
    </div>
  );
}

function ToggleBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-background text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

function DriftTable({
  rows,
  onRevert,
  onPlanchar,
}: {
  rows: ConfigDriftRow[];
  onRevert: (t: RevertTarget) => void;
  onPlanchar?: (id: string, label: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="grid grid-cols-12 gap-3 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
        <div className="col-span-2">Agencia</div>
        <div className="col-span-2">Equipo</div>
        <div className="col-span-2">Campo</div>
        <div className="col-span-2">Actual</div>
        <div className="col-span-2">Esperado</div>
        <div className="col-span-1 text-center">Revirtió</div>
        <div className="col-span-1 text-right">Acción</div>
      </div>
      {rows.map((r) => (
        <DriftRow
          key={`${r.device_id}:${r.field}`}
          row={r}
          onRevert={onRevert}
          onPlanchar={onPlanchar}
        />
      ))}
    </div>
  );
}

function DriftRow({
  row,
  onRevert,
  onPlanchar,
}: {
  row: ConfigDriftRow;
  onRevert: (t: RevertTarget) => void;
  onPlanchar?: (id: string, label: string) => void;
}) {
  const takeSnapshot = useTakeSnapshot();
  return (
    <div className="grid grid-cols-12 items-center gap-3 border-b px-4 py-3 text-sm last:border-b-0">
      <div className="col-span-2 min-w-0">
        <div className="truncate font-medium">{row.site}</div>
        {row.sin_datos ? (
          <span className="inline-flex items-center rounded-md bg-slate-500/15 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300">
            ○ Sin datos
          </span>
        ) : (
          <SeverityBadge value={row.severity} />
        )}
      </div>
      <div className="col-span-2 min-w-0">
        <div className="truncate">{row.position ?? "—"}</div>
        <div className="truncate font-mono text-xs text-muted-foreground">
          {row.ip}
        </div>
      </div>
      <div className="col-span-2 flex items-center gap-2 min-w-0">
        <FieldIcon field={row.field} />
        <span className="truncate">{row.field_label}</span>
      </div>
      <div className="col-span-2 min-w-0">
        <span
          title={row.actual || undefined}
          className="block truncate rounded bg-rose-500/5 px-2 py-1 font-mono text-xs text-rose-700 dark:text-rose-400"
        >
          {row.actual || "—"}
        </span>
      </div>
      <div className="col-span-2 min-w-0">
        <span className="block truncate rounded bg-emerald-500/5 px-2 py-1 font-mono text-xs text-emerald-700 dark:text-emerald-400">
          {row.expected || "—"}
        </span>
      </div>
      <div className="col-span-1 text-center">
        {row.reverted_count > 0 ? (
          <span
            title={`Ha revertido ${row.reverted_count} veces — posible causa de raíz (HikCentral)`}
            className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400"
          >
            <AlertTriangle className="size-3" />×{row.reverted_count}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </div>
      <div className="col-span-1 text-right">
        {/* Sin datos: no aplica Revertir ni Planchar (no hay config que leer);
            la única acción útil es re-cosechar por si ya respondió. */}
        {!row.sin_datos && (
          <button
            type="button"
            onClick={() =>
              onRevert({
                field: row.field,
                fieldLabel: row.field_label,
                deviceIds: [row.device_id],
                label: `${row.site} ${row.position ?? ""} · ${row.field_label}`.trim(),
                helper: `Re-aplica ${row.expected} (actual: ${row.actual}).`,
              })
            }
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted"
          >
            <RotateCcw className="size-3.5" /> Revertir
          </button>
        )}
        {!row.sin_datos && onPlanchar ? (
          <button
            type="button"
            onClick={() =>
              onPlanchar(row.device_id, `${row.site} · ${row.ip}`)
            }
            className="ml-1.5 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted"
          >
            <Wrench className="size-3.5" /> Planchar
          </button>
        ) : null}
        <button
          type="button"
          title="Recosechar este equipo ahora y actualizar su fila"
          onClick={() => takeSnapshot.mutate(row.device_id)}
          disabled={takeSnapshot.isPending}
          className="ml-1.5 inline-flex items-center rounded-md border px-1.5 py-1 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-60"
        >
          <RefreshCw
            className={cn("size-3.5", takeSnapshot.isPending && "animate-spin")}
          />
        </button>
      </div>
    </div>
  );
}

function DriftBySite({
  rows,
  onRevert,
  onPlanchar,
}: {
  rows: ConfigDriftRow[];
  onRevert: (t: RevertTarget) => void;
  onPlanchar?: (id: string, label: string) => void;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, ConfigDriftRow[]>();
    for (const r of rows) {
      const arr = map.get(r.site) ?? [];
      arr.push(r);
      map.set(r.site, arr);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  return (
    <div className="space-y-4">
      {grouped.map(([site, siteRows]) => (
        <div key={site} className="overflow-hidden rounded-md border bg-card">
          <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2">
            <span className="font-medium">{site}</span>
            <span className="text-xs text-muted-foreground">
              {siteRows.length} {siteRows.length === 1 ? "cambio" : "cambios"}
            </span>
          </div>
          {siteRows.map((r) => (
            <DriftRow
              key={`${r.device_id}:${r.field}`}
              row={r}
              onRevert={onRevert}
              onPlanchar={onPlanchar}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-md border bg-card p-10 text-center">
      <p className="text-sm font-medium">Sin cambios detectados</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Todos los equipos auditados coinciden con su configuración esperada.
      </p>
    </div>
  );
}


// ── Avance de la cosecha ────────────────────────────────────────────────────

/** Segundos legibles: 2.1 s, 34 s, 3m 12s. */
function duracion(ms: number | null | undefined): string {
  if (ms == null) return "";
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)} s`;
  if (ms < 60_000) return `${Math.round(ms / 1000)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

const ICONO_ESTADO: Record<string, string> = {
  ok: "✓",
  sin_datos: "—",
  offline: "✕",
  error: "!",
};

/**
 * Que esta haciendo la cosecha ahora mismo, y como termino.
 *
 * Contesta las tres preguntas del operador: por donde va, que equipo esta
 * bajando en este instante, y como quedo cada uno. El desglose final separa
 * los `ok` de los mudos a proposito: un equipo que no respondio NO es un
 * equipo conforme, y sumarlos fue el bug del #142.
 */
function AvanceCosecha({
  est,
  onCerrar,
}: {
  est: RecosechaEstado;
  onCerrar: () => void;
}) {
  const total = est.total ?? 0;
  const hechos = est.hechos ?? 0;
  const pct = total > 0 ? Math.round((hechos / total) * 100) : 0;
  const corriendo = !est.terminado;
  const porEstado = est.por_estado ?? {};
  const desglose = Object.entries(porEstado)
    .filter(([e]) => e !== "ok")
    .map(([e, n]) => `${n} ${e}`)
    .join(" · ");
  const variosPerfiles = (est.perfiles_activos?.length ?? 0) > 1;

  return (
    <div
      className={cn(
        "space-y-2 rounded-md border px-3 py-2 text-sm",
        corriendo
          ? "border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-300"
          : "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {corriendo && <Loader2 className="size-4 shrink-0 animate-spin" />}
          <span className="font-medium">
            {corriendo ? "Cosechando" : "Cosecha terminada"} — {hechos} de{" "}
            {total}
          </span>
          <span className="tabular-nums opacity-80">
            {est.logrados ?? 0} ok{desglose ? ` · ${desglose}` : ""}
          </span>
          <span className="opacity-70">
            {est.segundos != null ? `· ${duracion(est.segundos * 1000)}` : ""}
          </span>
        </div>
        {!corriendo && (
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar aviso"
            className="shrink-0 rounded px-1.5 opacity-70 transition-opacity hover:opacity-100"
          >
            ✕
          </button>
        )}
      </div>

      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-current/15"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Avance de la cosecha"
      >
        <div
          className="h-full rounded-full bg-current transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {corriendo && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span>
            <span className="opacity-70">Bajando: </span>
            {est.bajando?.length ? est.bajando.join(", ") : "—"}
          </span>
          {est.ultimo && (
            <span>
              <span className="opacity-70">Último: </span>
              {est.ultimo.nombre} {ICONO_ESTADO[est.ultimo.estado] ?? ""}{" "}
              {duracion(est.ultimo.ms)}
            </span>
          )}
        </div>
      )}

      {!corriendo && (est.por_estado?.ok ?? 0) < total && (
        <p className="text-xs opacity-80">
          Los que no dicen «ok» NO cuentan como conformes: AROC no pudo leerlos.
        </p>
      )}

      {est.perfil && (
        <p className="text-xs opacity-80">
          <span className="opacity-70">Perfil: </span>
          {est.perfil}
        </p>
      )}

      {variosPerfiles && !est.perfil && (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
          ⚠ Hay {est.perfiles_activos?.length} perfiles activos sobre estos
          equipos ({est.perfiles_activos?.join(", ")}). Corre el más antiguo —
          pausa el que no quieras, o pide uno por su id.
        </p>
      )}
    </div>
  );
}
